/**
 * fish-inventory.js — shared live-availability poller (PondKing.FishRefresh).
 *
 * Any element with data-fish-refresh="<section.id>:<node_id>" gets <node_id>
 * re-fetched via the Section Rendering API (?section_id=) on an interval and
 * on tab refocus. The node's innerHTML and root data-* attributes are synced
 * from the fresh render, then a `fish:refreshed` document event fires with
 * {sectionId, nodeId, container}.
 *
 * Deliberately NOT theme.AjaxRenderer — that caches section HTML by URL,
 * which is exactly wrong for live inventory.
 *
 * Opt-in contract (emit both from Liquid; never hardcode section ids):
 *   data-fish-refresh="{{ section.id }}:MyNodeId"
 *   data-fish-refresh-interval="45000"   (optional, ms, min 30000)
 *   data-fish-countdown                  (optional, any element: status text)
 *
 * Loaded by fish-collection, fish-cart-status and fish-pickup-info sections;
 * double-loading is safe (self-guarded singleton).
 */
(function () {
  'use strict';

  window.PondKing = window.PondKing || {};
  if (window.PondKing.FishRefresh) return;

  var DEFAULT_INTERVAL = 45000;
  var MIN_INTERVAL = 30000;

  var strings = (window.PondKingFish && window.PondKingFish.strings) || {};
  var generation = 0;
  var controller = null;
  var timer = null;
  var lastUpdated = null;
  var nextAt = null;

  function targets() {
    // Re-queried every cycle so swapped nodes and editor changes stay correct.
    var els = document.querySelectorAll('[data-fish-refresh]');
    var list = [];
    for (var i = 0; i < els.length; i++) {
      var parts = (els[i].getAttribute('data-fish-refresh') || '').split(':');
      if (parts.length !== 2 || !parts[0] || !parts[1]) continue;
      var interval = parseInt(els[i].getAttribute('data-fish-refresh-interval'), 10);
      list.push({
        sectionId: parts[0],
        nodeId: parts[1],
        interval: isNaN(interval) ? DEFAULT_INTERVAL : interval
      });
    }
    return list;
  }

  function currentInterval() {
    var list = targets();
    if (!list.length) return DEFAULT_INTERVAL;
    var min = Infinity;
    for (var i = 0; i < list.length; i++) {
      if (list[i].interval < min) min = list[i].interval;
    }
    return Math.max(MIN_INTERVAL, min);
  }

  function buildUrl(sectionId) {
    // Preserve the current query string (?page=2, sort_by) so paginated
    // collection renders refresh in place; cache-bust every request.
    var params = new URLSearchParams(window.location.search);
    params.set('section_id', sectionId);
    params.set('_fts', String(Date.now()));
    return window.location.pathname + '?' + params.toString();
  }

  function syncDataAttributes(live, fresh) {
    // Root data-* attributes carry machine-readable state (data-fish-blocked
    // etc.) — add/update from fresh, and drop ones the fresh render omitted.
    var i, name;
    var seen = {};
    for (i = 0; i < fresh.attributes.length; i++) {
      name = fresh.attributes[i].name;
      if (name.indexOf('data-') === 0) {
        seen[name] = true;
        live.setAttribute(name, fresh.attributes[i].value);
      }
    }
    var stale = [];
    for (i = 0; i < live.attributes.length; i++) {
      name = live.attributes[i].name;
      if (name.indexOf('data-') === 0 && !seen[name]) stale.push(name);
    }
    for (i = 0; i < stale.length; i++) live.removeAttribute(stale[i]);
  }

  // Returns a promise resolving once this cycle's swaps are done — the
  // checkout confirmation flow awaits it as a live inventory re-check.
  function refresh() {
    var list = targets();
    if (!list.length) { schedule(); return Promise.resolve(); }
    if (window.Shopify && window.Shopify.designMode) { schedule(); return Promise.resolve(); }
    if (document.hidden) return Promise.resolve(); // resumed by visibilitychange

    var gen = ++generation;
    if (controller) controller.abort();
    controller = new AbortController();

    var bySection = {};
    for (var i = 0; i < list.length; i++) {
      (bySection[list[i].sectionId] = bySection[list[i].sectionId] || []).push(list[i]);
    }

    var fetches = Object.keys(bySection).map(function (sectionId) {
      return fetch(buildUrl(sectionId), {
        signal: controller.signal,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('section fetch ' + response.status);
          return response.text();
        })
        .then(function (html) {
          if (gen !== generation) return; // stale response: drop
          var doc = new DOMParser().parseFromString(html, 'text/html');
          bySection[sectionId].forEach(function (target) {
            var fresh = doc.getElementById(target.nodeId);
            var live = document.getElementById(target.nodeId);
            if (!fresh || !live) return;
            // Swapping under the user's cursor drops focus mid-keystroke, but
            // skipping the swap would let the table go stale while claiming
            // freshness — so swap AND restore focus/value to the same control
            // (re-found by data-variant-id or id) afterwards.
            var focusKey = null;
            var focusValue = null;
            var active = document.activeElement;
            if (active && live.contains(active) &&
                /^(INPUT|SELECT|TEXTAREA|BUTTON)$/.test(active.tagName)) {
              if (active.getAttribute('data-variant-id')) {
                focusKey = active.tagName.toLowerCase() +
                  '[data-variant-id="' + active.getAttribute('data-variant-id') + '"]';
              } else if (active.getAttribute('data-slot-handle')) {
                // Pickup-slot radios (checked state is re-asserted separately
                // by fish-pickup.js after the swap).
                focusKey = active.tagName.toLowerCase() +
                  '[data-slot-handle="' + active.getAttribute('data-slot-handle') + '"]';
              } else if (active.getAttribute('data-pickup-day')) {
                // Pickup-day chips (the viewed day is re-applied by
                // fish-pickup.js on fish:refreshed).
                focusKey = active.tagName.toLowerCase() +
                  '[data-pickup-day="' + active.getAttribute('data-pickup-day') + '"]';
              } else if (active.id) {
                focusKey = '#' + active.id;
              }
              if (!focusKey) return; // unidentifiable control — skip this cycle
              focusValue = 'value' in active ? active.value : null;
            }
            live.innerHTML = fresh.innerHTML;
            if (focusKey) {
              var refocus = live.querySelector(focusKey);
              if (refocus && !refocus.disabled) {
                if (focusValue !== null && 'value' in refocus) refocus.value = focusValue;
                try { refocus.focus({ preventScroll: true }); } catch (err) { refocus.focus(); }
              }
            }
            syncDataAttributes(live, fresh);
            document.dispatchEvent(new CustomEvent('fish:refreshed', {
              detail: { sectionId: sectionId, nodeId: target.nodeId, container: live }
            }));
          });
        });
    });

    return Promise.all(fetches)
      .catch(function () { /* aborted or offline — retry next cycle */ })
      .then(function () {
        if (gen !== generation) return;
        lastUpdated = Date.now();
        schedule();
      });
  }

  function schedule() {
    clearTimeout(timer);
    var wait = currentInterval();
    nextAt = Date.now() + wait;
    timer = setTimeout(refresh, wait);
  }

  function countdownTick() {
    var els = document.querySelectorAll('[data-fish-countdown]');
    if (!els.length) return;
    var text = '';
    if (lastUpdated && nextAt) {
      var remain = Math.max(0, Math.round((nextAt - Date.now()) / 1000));
      if (remain <= 1) {
        text = strings.updatedJustNow || '';
      } else if (strings.refreshCountdown) {
        text = strings.refreshCountdown.replace('[seconds]', String(remain));
      }
    }
    for (var i = 0; i < els.length; i++) els[i].textContent = text;
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) refresh();
  });

  // Cart quantity changes re-render line items without a reload; re-poll so
  // availability banners and slot counts stay honest.
  var cartUpdatedTimer = null;
  document.addEventListener('cart:updated', function () {
    clearTimeout(cartUpdatedTimer);
    cartUpdatedTimer = setTimeout(refresh, 800);
  });

  function init() {
    if (!targets().length) return;
    lastUpdated = Date.now(); // the server render counts as fresh
    schedule();
    setInterval(countdownTick, 1000);
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  window.PondKing.FishRefresh = { refreshNow: refresh };
})();
